import { StringFormat } from "@packages/common";
import type { FilterDTO, FilterItemDTO } from "../../search";

export abstract class OpportunitiesTransformer {
  static readonly opportunitiesOrder = {
    "Possui WhatsApp": "Conversar por WhatsApp",
    "Responde Rapido": "Responde rápido",
    "Com Financiamento": "Com financiamento",
    Vistoriado: "Vistoriado",
    "Visão360°": "Visão 360°",
    "Super Preco": "Abaixo da Fipe",
    "Troca com troco": "Troca com troco",
    "Car Delivery": "Car Delivery",
  };
  
  /**
   * Transforms a given `FilterDTO` for "Opportunities" by ordering the items based on a predefined
   * order in `opportunitiesOrder`. Only items matching the predefined labels are included in the result.
   *
   * @param {FilterDTO} filter - The filter object to transform.
   * @returns {FilterDTO} - The transformed filter object with items ordered as specified in `opportunitiesOrder`.
   */
  static transform(filter: FilterDTO): FilterDTO {
    if (!filter || filter.name !== "Opportunities") return filter;
    const { formatSlug } = StringFormat;

    const orderedItems = Object.entries(
      OpportunitiesTransformer.opportunitiesOrder,
    )
      .map(([key, orderedLabel]) => {
        const slug = formatSlug(key);
        const matchedItem = filter.items.find(
          (item) => formatSlug(item.name) === slug,
        );
        return matchedItem
          ? {
              label: orderedLabel,
              name: matchedItem.name,
              count: matchedItem.count,
              description: matchedItem?.description ?? "",
            }
          : null;
      })
      .filter((item) => item !== null) as FilterItemDTO[];

    return {
      ...filter,
      items: orderedItems,
    };
  }

  /**
   * Transforms an array of opportunities from URL parameters by mapping them to display labels
   * according to predefined mappings in `opportunitiesOrder`, comparing slugged values.
   *
   * @param {string[]} opportunities - The list of opportunity strings from URL parameters.
   * @returns {Array<{ label: string, value: string }>} - An array of objects with `label` and `value` for each opportunity.
   */
  static transformFromUrl(
    opportunities: string[],
  ): Array<{ label: string; value: string }> {
    const { formatSlug } = StringFormat;
    const { opportunitiesOrder: list } = OpportunitiesTransformer;

    return opportunities.map((opportunity) => {
      const sluggedOpportunity = formatSlug(opportunity);
      const matchedKey = Object.keys(list).find(
        (key) => formatSlug(key) === sluggedOpportunity,
      );

      const label =
        matchedKey && matchedKey in list
          ? list[matchedKey as keyof typeof list]
          : opportunity;

      return {
        label,
        value: opportunity,
      };
    });
  }
}
